import { useTranslation } from 'react-i18next';
import { AccessComponentEnum } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { Colors } from '@lib/enums/colors';
import { APP_URLS } from 'constants/urls';
import useWidgetAccessControl from 'routes/Home/hooks/useWidgetAccessControl';
import isWithinOneWeek from 'routes/Home/utils/isWithinOneWeek';
import NewBadge from '../../NewBadge';
import styles from './QuickCreationWidget.module.scss';

interface QuickCreationButton {
  key: string;
  icon: string;
  labelKey: string;
  url: string;
  /** Access component required to use this button */
  accessComponent?: AccessComponentEnum;
}

const QUICK_CREATION_BUTTONS: QuickCreationButton[] = [
  {
    key: 'task',
    icon: 'task_alt',
    labelKey: 'task',
    url: APP_URLS.dashboard.tasks.add.path,
    accessComponent: AccessComponentEnum.TasksManagement,
  },
  {
    key: 'intervention',
    icon: 'build',
    labelKey: 'intervention',
    url: APP_URLS.dashboard.interventions.add.path,
    accessComponent: AccessComponentEnum.InterventionsManagement,
  },
  {
    key: 'formTemplate',
    icon: 'checklist',
    labelKey: 'form-template',
    url: APP_URLS.dashboard.formTemplates.add.path,
    accessComponent: AccessComponentEnum.TasksManagement,
  },
  {
    key: 'asset',
    icon: 'inventory',
    labelKey: 'asset',
    url: APP_URLS.dashboard.assets.add.path,
    accessComponent: AccessComponentEnum.AssetsManagement,
  },
  {
    key: 'site',
    icon: 'location_on',
    labelKey: 'site',
    url: APP_URLS.dashboard.sites.add.path,
    accessComponent: AccessComponentEnum.SitesManagement,
  },
  {
    key: 'teamMember',
    icon: 'person',
    labelKey: 'team-member',
    url: APP_URLS.dashboard.teams.add.path,
    accessComponent: AccessComponentEnum.UsersManagement,
  },
];

interface ButtonItem {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

export interface QuickCreationWidgetButtons {
  task?: ButtonItem | null;
  intervention?: ButtonItem | null;
  formTemplate?: ButtonItem | null;
  asset?: ButtonItem | null;
  site?: ButtonItem | null;
  teamMember?: ButtonItem | null;
}

export interface QuickCreationWidgetProps {
  buttons?: QuickCreationWidgetButtons;
  /** Whether current home page is the default one - badges only show on default */
  isDefaultHomePage?: boolean;
}

export default function QuickCreationWidget({
  buttons,
  isDefaultHomePage = false,
}: QuickCreationWidgetProps) {
  const { t } = useTranslation();
  const { isInterventionsLockedByPlan, isComponentAvailable } =
    useWidgetAccessControl();

  // Check if user has access to a specific button based on its access component
  const hasButtonAccess = (button: QuickCreationButton): boolean => {
    if (!button.accessComponent) return true;
    return isComponentAvailable(button.accessComponent);
  };

  // Check if a button is locked due to plan restriction (only applies to intervention)
  const isButtonLockedByPlan = (button: QuickCreationButton): boolean => {
    return button.key === 'intervention' && isInterventionsLockedByPlan;
  };

  // Filter buttons based on configuration, show all if no config provided
  const visibleButtons = QUICK_CREATION_BUTTONS.filter((button) => {
    if (!buttons) return true;
    const buttonConfig =
      buttons[button.key as keyof QuickCreationWidgetButtons];
    return buttonConfig?.enabled === true;
  });

  // Get badge visibility for a button based on its updatedAt
  const shouldShowButtonBadge = (buttonKey: string): boolean => {
    if (!isDefaultHomePage || !buttons) return false;
    const buttonConfig = buttons[buttonKey as keyof QuickCreationWidgetButtons];
    return isWithinOneWeek(buttonConfig?.updatedAt);
  };

  const handleClick = (url: string) => {
    window.open(url, '_blank')?.focus();
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollableContent}>
        <div className={styles.grid}>
          {visibleButtons.map((button) => {
            const hasAccess = hasButtonAccess(button);
            const lockedByPlan = isButtonLockedByPlan(button);

            // Show blocked state if no user access or locked by plan
            if (!hasAccess || lockedByPlan) {
              // Use plan restriction text for intervention locked by plan,
              // otherwise use regular no-access text
              const titleKey = lockedByPlan
                ? 'home.widget-plan-restriction-title'
                : 'home.widget-no-access-title';
              const descKey = lockedByPlan
                ? 'home.widget-plan-restriction-description'
                : 'home.widget-no-access-description';

              return (
                <div key={button.key} className={styles.blockedItem}>
                  <Typography variant="body" className={styles.blockedTitle}>
                    {t(titleKey)}
                  </Typography>
                  <Tooltip body={t(descKey)}>
                    <MaterialIcon
                      cursor="help"
                      icon="info"
                      className={styles.infoIcon}
                      symbolsOutlined
                    />
                  </Tooltip>
                </div>
              );
            }

            return (
              <div key={button.key} className={styles.item}>
                <IconSurface
                  icon={button.icon}
                  color={Colors.Blue}
                  shape="circle"
                  size="sm"
                  symbolsOutlined
                />
                <div className={styles.itemContent}>
                  <Typography variant="body" className={styles.itemTitle}>
                    {t(button.labelKey)}
                  </Typography>
                  {shouldShowButtonBadge(button.key) && (
                    <div>
                      <NewBadge />
                    </div>
                  )}
                </div>
                <IconButton
                  icon="add"
                  type={IconButtonTypes.PrimaryOutlined}
                  onClick={() => handleClick(button.url)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
